import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtLogCompact } from "@/lib/tz";
import BulkEmailCompose from "@/components/BulkEmailCompose";
import { sanitizeHtml } from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Send, History, Loader2, RotateCcw, Copy, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";
const EMAIL_RE = /^[^@\s,;<>]+@[^@\s,;<>]+\.[^@\s,;<>]+$/;

export default function BulkEmail() {
  const [tab, setTab] = useState("compose");
  const [senders, setSenders] = useState([]);
  const [seed, setSeed] = useState({ nonce: 0 });
  useEffect(() => {
    api.bulkEmailSenders().then((r) => setSenders(r.senders || [])).catch(() => toast.error("Could not load sender mailboxes."));
  }, []);
  // Start (or restart) the composer from a template or a past campaign.
  const openCompose = (s) => { setSeed({ ...(s || {}), nonce: Date.now() }); setTab("compose"); };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="bulk-email-page">
      <div className="flex items-center gap-2.5 mb-1">
        <Mail className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Bulk email</h1>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Send one personalised email to many people from a chosen mailbox. Emails go out gradually through the
        email queue, and each recipient's delivery is tracked.
      </p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="compose" data-testid="bulk-tab-compose"><Send className="h-3.5 w-3.5 mr-1.5" />New email</TabsTrigger>
          <TabsTrigger value="history" data-testid="bulk-tab-history"><History className="h-3.5 w-3.5 mr-1.5" />Sent campaigns</TabsTrigger>
          <TabsTrigger value="templates" data-testid="bulk-tab-templates"><FileText className="h-3.5 w-3.5 mr-1.5" />Templates</TabsTrigger>
        </TabsList>
        {/* Kept mounted (just hidden) so a half-written email survives a look at the other tabs. */}
        <TabsContent value="compose" forceMount className={`mt-4 ${tab === "compose" ? "" : "hidden"}`}>
          <BulkEmailCompose key={seed.nonce} senders={senders} seed={seed} onSent={() => { setSeed({ nonce: Date.now() }); setTab("history"); }} />
        </TabsContent>
        <TabsContent value="history" className="mt-4"><Campaigns onUse={openCompose} /></TabsContent>
        <TabsContent value="templates" className="mt-4"><Templates senders={senders} onUse={openCompose} /></TabsContent>
      </Tabs>
    </div>
  );
}

const STATUS_STYLE = { sent: "bg-emerald-50 text-emerald-700", failed: "bg-red-50 text-red-700", queued: "bg-amber-50 text-amber-700", bounced: "bg-red-50 text-red-700" };

const seedFromCampaign = (c, recipients) => ({
  sender_id: c.sender_id, reply_to: c.reply_to || "", subject: c.subject, body_html: c.body_html, recipients,
});

function Campaigns({ onUse }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = () => api.bulkEmailCampaigns()
    .then((r) => { setRows(r.campaigns || []); setFailed(false); })
    .catch(() => { setRows((cur) => cur || []); setFailed(true); });
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const view = async (id) => {
    try { setOpen(await api.bulkEmailCampaign(id)); } catch { toast.error("Could not open that campaign."); }
  };
  const copyAgain = async (id) => {
    try {
      const d = await api.bulkEmailCampaign(id);
      onUse(seedFromCampaign(d.campaign, []));
      toast.success("Copied. Add recipients and send.");
    } catch { toast.error("Could not copy that campaign."); }
  };

  if (rows === null) return <div className="text-xs text-slate-400">Loading…</div>;
  const bar = <ReplyTrackingBar onChecked={load} />;
  if (failed && !rows.length) return <div className="text-xs text-red-600" data-testid="bulk-campaigns-error">Could not load sent campaigns. Please refresh, or contact support if it keeps happening.</div>;
  if (!rows.length) return <div className="text-xs text-slate-400">No bulk emails sent yet.</div>;
  return (
    <>
      {bar}
      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="bulk-campaigns">
        {rows.map((c) => (
          <div key={c.id} className="flex items-start hover:bg-slate-50">
            <button onClick={() => view(c.id)} className="flex-1 text-left px-4 py-3 min-w-0" data-testid={`bulk-campaign-${c.id}`}>
              <div className="text-sm font-medium text-slate-800">{c.name}</div>
              <div className="text-[11px] text-slate-500">
                {fmtLogCompact(c.created_at)} · {c.created_by_name} · from {c.from_email} · {c.total} recipients
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                <Badge className={STATUS_STYLE.sent}>{c.counts.sent} sent</Badge>
                {c.track_opens && <Badge className="bg-sky-50 text-sky-700">{c.counts.opened} opened (approx.)</Badge>}
                {c.track_clicks && <Badge className="bg-violet-50 text-violet-700">{c.counts.clicked} clicked</Badge>}
                {c.counts.replied > 0 && <Badge className="bg-emerald-50 text-emerald-700">{c.counts.replied} replied</Badge>}
                {c.counts.bounced > 0 && <Badge className={STATUS_STYLE.bounced}>{c.counts.bounced} bounced</Badge>}
                {c.counts.queued > 0 && <Badge className={STATUS_STYLE.queued}>{c.counts.queued} queued</Badge>}
                {c.counts.failed > 0 && <Badge className={STATUS_STYLE.failed}>{c.counts.failed} failed</Badge>}
              </div>
            </button>
            <Button variant="ghost" size="sm" className="m-2 h-8 text-xs shrink-0" onClick={() => copyAgain(c.id)} data-testid={`bulk-use-again-${c.id}`}>
              <Copy className="h-3.5 w-3.5 mr-1.5" />Use again
            </Button>
          </div>
        ))}
      </div>
      {open && (
        <CampaignDialog data={open} onClose={() => setOpen(null)} onUse={onUse}
          onChanged={async () => { setOpen(await api.bulkEmailCampaign(open.campaign.id)); load(); }} />
      )}
    </>
  );
}

function CampaignDialog({ data, onClose, onUse, onChanged }) {
  const { campaign: c, recipients } = data;
  const retryable = recipients.filter((r) => r.status === "bounced" || r.status === "failed");
  const [sel, setSel] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const edit = (id, v) => { setEdits({ ...edits, [id]: v }); setSel((s) => new Set(s).add(id)); };

  const retry = async () => {
    const items = [...sel].map((id) => ({ recipient_id: id, email: (edits[id] || "").trim() || undefined }));
    const bad = items.find((i) => i.email && !EMAIL_RE.test(i.email));
    if (bad) { toast.error(`“${bad.email}” is not a valid email address.`); return; }
    setBusy(true);
    try {
      const res = await api.bulkEmailRetry(c.id, { items });
      if (res.queued) toast.success(`Re-queued ${res.queued} email${res.queued === 1 ? "" : "s"}.`);
      if (res.skipped?.length) toast.error(res.skipped.map((s) => `${s.email || ""} ${s.reason}`.trim()).join(" · "));
      setSel(new Set()); setEdits({});
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry."); }
    finally { setBusy(false); }
  };

  const copy = (withRecipients) => {
    const keep = recipients.filter((r) => !(r.status === "bounced" && r.bounce_type !== "soft"));
    const list = withRecipients ? keep.map((r) => ({ email: r.email, first_name: r.first_name, name: r.name, kind: r.kind, ref: r.ref })) : [];
    onUse(seedFromCampaign(c, list));
    toast.success(withRecipients
      ? `Copied with ${list.length} recipient${list.length === 1 ? "" : "s"}${keep.length < recipients.length ? ` (left out ${recipients.length - keep.length} that bounced)` : ""}.`
      : "Copied. Add recipients and send.");
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-campaign-dialog">
        <DialogHeader><DialogTitle>{c.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => copy(false)} data-testid="bulk-copy-content"><Copy className="h-3.5 w-3.5 mr-1.5" />Use as new email</Button>
          <Button variant="outline" size="sm" onClick={() => copy(true)} data-testid="bulk-copy-recipients"><Copy className="h-3.5 w-3.5 mr-1.5" />Use with same recipients</Button>
        </div>
        {retryable.length > 0 && (
          <div className="rounded-md border border-red-100 bg-red-50/50 px-3 py-2 text-[11px] text-slate-600 flex flex-wrap items-center gap-2" data-testid="bulk-retry-bar">
            <span>{retryable.length} bounced or failed. Fix an address if it was wrong, tick the ones to resend, then retry.</span>
            <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2"
              onClick={() => setSel(sel.size === retryable.length ? new Set() : new Set(retryable.map((r) => r.id)))}>
              {sel.size === retryable.length ? "Clear selection" : "Select all"}
            </Button>
            <Button size="sm" className="h-7 text-xs ml-auto text-white" style={{ background: CRIMSON }} disabled={!sel.size || busy} onClick={retry} data-testid="bulk-retry">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}Retry selected ({sel.size})
            </Button>
          </div>
        )}
        <div className="divide-y divide-slate-100 text-xs">
          {recipients.map((r) => {
            const can = r.status === "bounced" || r.status === "failed";
            return (
              <div key={r.id} className="py-1.5 flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  {can && <input type="checkbox" className="mt-1" checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`bulk-retry-check-${r.id}`} />}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-slate-800">{r.name}</span>{" "}
                    {can ? (
                      <Input value={edits[r.id] ?? r.email} onChange={(e) => edit(r.id, e.target.value)} className="h-7 mt-0.5 text-xs max-w-xs" data-testid={`bulk-retry-email-${r.id}`} />
                    ) : <span className="text-slate-400">{r.email}</span>}
                    {r.original_email && <div className="text-[10px] text-slate-400">address corrected from {r.original_email}</div>}
                    {r.retry_count > 0 && <div className="text-[10px] text-slate-400">retried {r.retry_count} time{r.retry_count === 1 ? "" : "s"}</div>}
                    {r.error && <div className="text-[10px] text-red-600">{r.error}</div>}
                    {r.status === "bounced" && <div className="text-[10px] text-red-600">{r.bounce_type === "soft" ? "Temporary bounce" : "Bounced"}: {r.bounce_reason}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap justify-end items-center gap-1.5 shrink-0">
                  {r.replied_at && <Badge className="bg-emerald-50 text-emerald-700" title={`${r.reply_count || 1} repl${(r.reply_count || 1) === 1 ? "y" : "ies"} · matched by ${r.reply_via === "subject" ? "address + subject" : "message id"}`}>replied {fmtLogCompact(r.replied_at)}</Badge>}
                  {r.auto_reply_at && !r.replied_at && <Badge className="bg-slate-100 text-slate-600" title="Out-of-office or other automatic reply">auto-reply</Badge>}
                  {r.clicked_at && <Badge className="bg-violet-50 text-violet-700" title={`${r.click_count || 1} click(s)`}>clicked {fmtLogCompact(r.clicked_at)}</Badge>}
                  {r.opened_at && <Badge className="bg-sky-50 text-sky-700" title={`${r.open_count || 1} open(s)`}>opened {fmtLogCompact(r.opened_at)}</Badge>}
                  <Badge className={STATUS_STYLE[r.status] || ""}>{r.status}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const PREVIEW_STYLES = "text-xs text-slate-600 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_a]:text-teal-700 [&_a]:underline";

function Templates({ senders, onUse }) {
  const [rows, setRows] = useState(null);
  const load = () => api.bulkEmailTemplates().then((r) => setRows(r.templates || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const label = useMemo(() => Object.fromEntries(senders.map((s) => [s.id, s.label])), [senders]);
  const del = async (t) => {
    if (!window.confirm(`Delete the template “${t.name}”? Emails already sent are not affected.`)) return;
    try { await api.bulkEmailDeleteTemplate(t.id); toast.success("Template deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };
  if (rows === null) return <div className="text-xs text-slate-400">Loading…</div>;
  if (!rows.length) return <div className="text-xs text-slate-400" data-testid="bulk-templates-empty">No templates yet. Write an email on the New email tab and choose “Save as template”.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="bulk-templates">
      {rows.map((t) => (
        <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{t.subject || "No subject"}{t.sender_id && label[t.sender_id] ? ` · from ${label[t.sender_id]}` : ""}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onUse({
                sender_id: t.sender_id, reply_to: t.reply_to || "", subject: t.subject, body_html: t.body_html, template: { id: t.id, name: t.name },
              })} data-testid={`bulk-template-use-${t.id}`}>Use</Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => del(t)} title="Delete template" data-testid={`bulk-template-delete-${t.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className={`mt-2 max-h-24 overflow-hidden ${PREVIEW_STYLES}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(t.body_html) }} />
        </div>
      ))}
    </div>
  );
}

function ReplyTrackingBar({ onChecked }) {
  const [info, setInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const load = () => api.bulkEmailReplyTracking().then(setInfo).catch(() => setInfo({ senders: [] }));
  useEffect(() => { load(); }, []);
  if (!info) return null;
  const on = info.senders.filter((s) => s.enabled);
  const errs = on.filter((s) => s.error);
  const last = on.map((s) => s.last_ok).filter(Boolean).sort().pop();
  const check = async () => {
    setChecking(true);
    try {
      const r = await api.bulkEmailCheckReplies();
      const n = (r.results || []).reduce((a, x) => a + x.replies + x.bounces, 0);
      toast.success(n ? `Found ${n} new repl${n === 1 ? "y/bounce" : "ies/bounces"}.` : "Checked. Nothing new.");
      load(); onChecked();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not check."); }
    finally { setChecking(false); }
  };
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600 flex flex-wrap items-center gap-2" data-testid="bulk-reply-bar">
      {!on.length ? (
        <span>Replies and bounces aren't being tracked. An admin can turn this on for a mailbox under Settings → Email.</span>
      ) : (
        <>
          <span>Replies &amp; bounces tracked for {on.map((s) => s.label).join(", ")}{last ? ` · last checked ${fmtLogCompact(last)}` : " · not checked yet"}. Checked every 5 minutes.</span>
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={check} disabled={checking} data-testid="bulk-check-replies">
            {checking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Check now
          </Button>
        </>
      )}
      {errs.map((s) => <span key={s.id} className="text-red-600 w-full">{s.label}: {s.error}</span>)}
    </div>
  );
}
